"use client"

import { useEffect, useCallback, useState } from "react"
import { useGoogleOneTapMutation } from "@/app/store/api/authApi"
import { useAppDispatch, useAppSelector } from "@/app/store/hooks"
import { setCredentials } from "@/app/store/slices/authSlice"
import { toast } from "sonner"
import Script from "next/script"
import { trackEvent, identifyUser } from "@/lib/mixpanel"

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: any) => void
                    prompt: (callback?: (notification: any) => void) => void
                    cancel: () => void
                }
            }
        }
    }
}

export function GoogleOneTapTrigger() {
    const { isAuthenticated } = useAppSelector((state) => state.auth)
    const [googleOneTap] = useGoogleOneTapMutation()
    const dispatch = useAppDispatch()
    // Don't load GSI script in automated/headless environments (Lighthouse, bots)
    const [shouldLoad, setShouldLoad] = useState(false)

    useEffect(() => {
        if (!navigator.webdriver && !isAuthenticated) {
            setShouldLoad(true)
        }
    }, [isAuthenticated])

    const handleOneTapResponse = useCallback(async (response: { credential: string }) => {
        try {
            const userData = await googleOneTap({ credential: response.credential }).unwrap()
            dispatch(setCredentials({ user: userData.user, token: userData.token }))
            if (userData.user?._id) {
                identifyUser(userData.user)
            }
            trackEvent("User Logged In", { method: "google_one_tap" })
            toast.success("Successfully logged in with Google")
        } catch (err: any) {
            console.error('Google One Tap failed:', err)
        }
    }, [googleOneTap, dispatch])

    const initializeGoogleOneTap = useCallback(() => {
        const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
        if (!clientId || !window.google || isAuthenticated) return

        window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleOneTapResponse,
            auto_select: false,
            use_fedcm_for_prompt: false,
        })

        window.google.accounts.id.prompt()
    }, [handleOneTapResponse, isAuthenticated])

    useEffect(() => {
        if (window.google && shouldLoad) {
            initializeGoogleOneTap()
        }
        return () => {
            window.google?.accounts.id.cancel()
        }
    }, [initializeGoogleOneTap, shouldLoad])

    if (!shouldLoad) return null

    return (
        <Script
            src="https://accounts.google.com/gsi/client"
            strategy="afterInteractive"
            onLoad={initializeGoogleOneTap}
        />
    )
}
